/* =========================================================
   HORTIGO OS V92.2 — assets/hg-market.js
   Market module (Bursă):
   - select cultură grouped by categorie (optgroup)
   - tier buttons (data-mod="m") -> basic/std/prem
   - uses HG.consume(), HG.buildPromptMarket(), HG.runAI()
   - listens hg:ready + hg:config-updated
========================================================= */

(() => {
  "use strict";

  function $(root, id) {
    return root ? root.querySelector(`#${CSS.escape(id)}`) : null;
  }

  function qsa(root, sel) {
    return Array.from(root ? root.querySelectorAll(sel) : []);
  }

  function safeText(el, txt) {
    if (el) el.textContent = String(txt ?? "");
  }

  function bootMarket() {
    const HG = window.HG;
    if (!HG || !HG.state || !HG.state.root) return;

    const root = HG.state.root;

    // Prevent double init
    if (root.getAttribute("data-hg-market-bound") === "1") return;
    root.setAttribute("data-hg-market-bound", "1");

    const mCrop = $(root, "mCrop");
    const mCropHelp = $(root, "mCropHelp");
    const btnRunM = $(root, "btnRunM");
    const outM = $(root, "out-m");

    const tierBtns = qsa(root, `.hg-tier[data-mod="m"]`);

    function applyOutReadability() {
      if (!outM || !HG.ui.applyOutputReadability) return;
      HG.ui.applyOutputReadability(outM);
    }

    // ------- Fill crops (with category) -------
    function fillCrops() {
      if (!mCrop) return;

      const crops = Array.isArray(HG.state.cfg?.crops) ? HG.state.cfg.crops : [];
      mCrop.innerHTML = "";

      const def = document.createElement("option");
      def.value = "";
      def.textContent = "-- Alege --";
      mCrop.appendChild(def);

      // group by cat
      const byCat = new Map();
      for (const c of crops) {
        const cat = String(c.cat || "General").trim() || "General";
        if (!byCat.has(cat)) byCat.set(cat, []);
        byCat.get(cat).push(c);
      }

      // sort cats + crops
      const cats = Array.from(byCat.keys()).sort((a, b) => a.localeCompare(b, "ro"));
      for (const cat of cats) {
        const og = document.createElement("optgroup");
        og.label = cat;

        const list = byCat.get(cat) || [];
        list.sort((a, b) => String(a.name).localeCompare(String(b.name), "ro"));

        for (const c of list) {
          const opt = document.createElement("option");
          opt.value = String(c.name || "");
          const reqTier = Number.isFinite(+c.tier) ? +c.tier : 0;
          const locked = reqTier > (HG.state.user?.tier ?? 0) || !!HG.state.user?.pending;
          opt.disabled = locked;
          opt.textContent = locked ? `🔒 ${c.name}` : c.name;
          og.appendChild(opt);
        }

        mCrop.appendChild(og);
      }

      const hint =
        HG.state.user?.pending
          ? "Plată incompletă: unele funcții sunt blocate."
          : (HG.state.user?.tier ?? 0) === 0
          ? "Tier 1 = 🔒 (membru), Tier 2 = 🔒 (premium)."
          : (HG.state.user?.tier ?? 0) === 1
          ? "Tier 2 = 🔒 (premium)."
          : "Ai acces complet.";

      safeText(mCropHelp, hint);

      if (!crops.length) {
        safeText(mCropHelp, "Nu există culturi în config (/get-config). Verifică VPS /get-config.");
      }
    }

    // ------- Tier buttons binding -------
    function bindTierButtons() {
      for (const btn of tierBtns) {
        btn.addEventListener("click", () => {
          if (btn.classList.contains("is-locked")) {
            alert(HG.state.user?.pending ? "Plata incompletă." : "Upgrade necesar!");
            if (typeof HG.navTo === "function") HG.navTo("wall");
            return;
          }

          const tier = btn.getAttribute("data-tier");
          if (!tier) return;

          tierBtns.forEach((b) => b.classList.remove("is-active"));
          btn.classList.add("is-active");

          HG.state.uiTier = HG.state.uiTier || {};
          HG.state.uiTier.m = tier;
        });
      }
    }

    // ------- Run -------
    async function runMarket() {
      const tier = String(HG.state.uiTier?.m || "basic");

      const crop = String(mCrop?.value || "").trim();
      if (!crop) {
        alert("Selectează o cultură pentru bursă!");
        return;
      }

      // compute cost
      const costs = HG.getCosts();
      const cost = tier === "basic" ? costs.basic : tier === "std" ? costs.std : costs.prem;

      if ((HG.state.user?.tok || 0) < cost) {
        alert(`Ai nevoie de ${cost} credite.`);
        if (typeof HG.navTo === "function") HG.navTo("wall");
        return;
      }

      const ok = await HG.consume(cost);
      if (!ok) {
        alert("Nu s-au putut consuma creditele.");
        return;
      }

      if (outM) outM.innerHTML = `<div class="hg-empty">Se generează…</div>`;
      HG.ui.setOutLoading(outM, true, "Consult bursa… verific piață, parități, logistică…");

      try {
        const prompt = HG.buildPromptMarket({ tier, crop });
        const safeHtml = await HG.runAI({ type: "TEXT", prompt });

        if (outM) outM.innerHTML = safeHtml || `<div class="hg-empty">Fără conținut.</div>`;
        applyOutReadability();
      } catch (e) {
        console.error("[HG Market] error:", e);
        if (outM) outM.innerHTML = `<div class="hg-empty">Eroare de conexiune.</div>`;
      } finally {
        HG.ui.setOutLoading(outM, false);
      }
    }

    // ------- Init -------
    function init() {
      HG.state.uiTier = HG.state.uiTier || {};
      if (!HG.state.uiTier.m) HG.state.uiTier.m = "basic";

      bindTierButtons();

      if (btnRunM) btnRunM.addEventListener("click", runMarket);

      // initial fill
      fillCrops();

      // refresh after config arrives/changes
      document.addEventListener("hg:config-updated", () => {
        try { fillCrops(); } catch (_) {}
      });
    }

    init();
  }

  function bootSafe() {
    if (window.HG?.state?.root) bootMarket();
  }

  // ✅ start after HG published
  document.addEventListener("hg:ready", () => {
    try { bootSafe(); } catch (_) {}
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootSafe);
  } else {
    bootSafe();
  }

  document.addEventListener("shopify:section:load", () => {
    try { bootSafe(); } catch (_) {}
  });
})();
